import type { Door, Lock, LockModel } from './types';
import { MODEL_LABEL } from './format';

/**
 * Regels voor "slot koppelen aan deur" in het portaal. Spiegelt public.link_lock_to_door (migratie 0010):
 * het slot hoort bij de club, hangt aan geen enkele deur, is niet defect/afgeschreven, en heeft het model dat de deur
 * verwacht (als de deur een verwacht model heeft). De database is de echte controle; dit is voor de keuzelijst.
 */

/** Sloten van de club die nog aan geen (actieve) deur hangen en gekoppeld kunnen worden. */
export function unlinkedLocks(locks: Lock[], doors: Door[]): Lock[] {
  const used = new Set(doors.filter((d) => !d.archived_at && d.lock_id).map((d) => d.lock_id!));
  return locks
    .filter((l) => (l.status === 'assigned' || l.status === 'installed') && !used.has(l.id))
    .sort((a, b) => MODEL_LABEL[a.model].localeCompare(MODEL_LABEL[b.model]) || (a.serial ?? '').localeCompare(b.serial ?? ''));
}

/** Waarom dit slot niet op deze deur past, of null als het past. */
export function modelMismatch(expected: LockModel | null | undefined, lock: Pick<Lock, 'model'>): string | null {
  if (!expected || expected === lock.model) return null;
  return `Deze deur verwacht een ${MODEL_LABEL[expected]}, dit slot is een ${MODEL_LABEL[lock.model]}.`;
}

export interface LockOption {
  lock: Lock;
  /** null = kan gekoppeld worden. */
  blocked: string | null;
}

/** Keuzelijst voor een deur: alle ongekoppelde sloten, passende eerst. */
export function lockOptionsForDoor(door: Door, locks: Lock[], doors: Door[]): LockOption[] {
  return unlinkedLocks(locks, doors)
    .map((lock) => ({ lock, blocked: modelMismatch(door.expected_model, lock) }))
    .sort((a, b) => Number(!!a.blocked) - Number(!!b.blocked));
}

/** Deuren zonder slot waar dit slot op past (keuzelijst vanuit "sloten zonder deur"). */
export function doorOptionsForLock(lock: Lock, doors: Door[]): Door[] {
  return doors
    .filter((d) => !d.archived_at && !d.lock_id && !modelMismatch(d.expected_model, lock))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Mag deze rol het slot van deze deur halen? Geïnstalleerd: alleen Antreja (platform_admin). */
export function canUnlink(lock: Pick<Lock, 'status'>, isPlatformAdmin: boolean): boolean {
  return lock.status !== 'installed' || isPlatformAdmin;
}

export const DOOR_KIND_LABEL: Record<Door['kind'], string> = { exterior: 'Buitendeur', interior: 'Binnendeur' };
